/**
 * `v-email`: an input that takes an e-mail address keeps it in lower case.
 *
 * E-mail addresses are matched without regard to letter case everywhere on
 * the backend (the SDK lowercases them before Cognito and the API, and the
 * backend hashes and compares them lowercased), so a capital letter typed here
 * never means anything. What it did do was make the same address look like two
 * different ones on screen, and leave a phone's auto-capitalised first letter
 * in the box. This directive lowercases what is typed or pasted as it arrives,
 * and tells mobile keyboards not to capitalise or correct.
 *
 * It listens in the capture phase, so it runs before the `v-model` and
 * `@input` handlers on the same element even though they are registered
 * first: they read the value after it is lowercased, and the page's state
 * holds the same text the box shows. Only a value that actually carries a
 * capital letter is rewritten, so ordinary typing is untouched (rewriting the
 * value moves the caret to the end on an e-mail-type input, where the browser
 * exposes no caret position; on a text input the caret is put back).
 *
 * Usage: `input(v-email type="email" ...)`. Registered on the app in main.ts.
 */
import type { Directive } from "vue";

function lowercaseInPlace(el: HTMLInputElement): void {
    const value = el.value;
    const lower = value.toLowerCase();
    if (value === lower) return;
    let start: number | null = null;
    let end: number | null = null;
    try {
        start = el.selectionStart;
        end = el.selectionEnd;
    } catch {
        // an e-mail-type input has no selection API
    }
    el.value = lower;
    if (start !== null && end !== null) {
        try {
            el.setSelectionRange(start, end);
        } catch {
            // same as above
        }
    }
}

export const emailInput: Directive<HTMLInputElement> = {
    created(el) {
        if (!el.hasAttribute("autocapitalize")) el.setAttribute("autocapitalize", "none");
        if (!el.hasAttribute("autocorrect")) el.setAttribute("autocorrect", "off");
        if (!el.hasAttribute("spellcheck")) el.setAttribute("spellcheck", "false");
        el.addEventListener("input", () => lowercaseInPlace(el), { capture: true });
    },
};
